import React from "react"
import { isValid } from "date-fns"
import { flatten, sum } from "lodash"
import { groupBy } from "~/utils/tsUtils"

import { filterOnSearchString } from "~/utils/search"
import { isAfter, isBefore } from "./datetime"
import { roundNumber } from "./helpers"
import { SIZE_DIMENSION_1_LABEL, SIZE_DIMENSION_2_LABEL } from "./sizeDimensions"
import { expectedGrowthFraction, expectedGrowthUnits } from "./growthMetrics"
import {
  changePct,
  forecastChangeRanges,
  type ForecastChangeFieldKey,
  type ForecastChanges,
  type ForecastRangeLabel,
} from "./forecastChangeMetrics"
import { roundForecast } from "./formatting"
import { calculateWeeksOnHand } from "./weeksOnHand"
import {
  deriveLifecycleStage,
  formatLifecycleStatus,
  isInactiveStatus,
  isLifecyclePhasedOut,
} from "~/utils/lifecycleUtils"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { type INpiVariantSummary } from "~/server/api/routers/npi"
import { filterIncomingOrders, getIncomingOrderExtraDataFilters } from "~/utils/incomingOrdersUtils"
export { matchesStringFilter } from "~/utils/string-filter"
import {
  ArrowsClockwise,
  CalendarBlank,
  ChartBar,
  ChartLine,
  CoatHanger,
  Factory,
  Flag,
  Hash,
  Hourglass,
  Package,
  Palette,
  Percent,
  Ruler,
  ShoppingCartSimple,
  Sliders,
  Sparkle,
  Tag,
  TextT,
  Timer,
  TreeStructure,
  TrendDown,
  TrendUp,
  TShirt,
  User,
  Users,
  Warning,
} from "@phosphor-icons/react"

import {
  splitFiltersIntoGroupUnits as splitGroupUnits,
  splitFiltersIntoOrGroups,
  type Filter,
  type FilterGate,
  type FilterGroupUnit as GroupUnit,
} from "./filterGroups"
export { splitFiltersIntoOrGroups, type Filter, type FilterGate }

export type FilterGroupUnit = GroupUnit<Filter>

export function splitFiltersIntoGroupUnits(filters: Filter[]): FilterGroupUnit[] {
  return splitGroupUnits(filters)
}

function isWithinGroupConnector(filters: Filter[], i: number): boolean {
  if (i <= 0) return false
  const cur = filters[i]
  const prev = filters[i - 1]
  return cur?.groupId != null && prev?.groupId === cur.groupId
}

export function setUniformGate(filters: Filter[], index: number, gate: FilterGate): Filter[] {
  const target = filters[index]
  if (!target || !isWithinGroupConnector(filters, index)) return filters
  return filters.map((filter, i) =>
    isWithinGroupConnector(filters, i) && filter.groupId === target.groupId
      ? { ...filter, gate }
      : filter,
  )
}

export function normalizeGates(filters: Filter[]): Filter[] {
  const groupGate = new Map<number, FilterGate>()
  for (let i = 1; i < filters.length; i++) {
    const cur = filters[i]
    if (isWithinGroupConnector(filters, i) && cur?.groupId != null && !groupGate.has(cur.groupId)) {
      groupGate.set(cur.groupId, cur.gate ?? "or")
    }
  }
  return filters.map((filter, i) => {
    if (i === 0) return filter.gate ? { ...filter, gate: undefined } : filter
    if (isWithinGroupConnector(filters, i) && filter.groupId != null) {
      return { ...filter, gate: groupGate.get(filter.groupId) ?? "or" }
    }
    return filter.gate === "and" ? filter : { ...filter, gate: "and" }
  })
}

function hasAdvancedFilterExpression(filters: Filter[]): boolean {
  return filters.some((f) => f.gate === "or" || f.groupId != null)
}

function nextGroupId(filters: Filter[]): number {
  const used = new Set(filters.map((f) => f.groupId).filter((g): g is number => g != null))
  let id = 1
  while (used.has(id)) id++
  return id
}

export function dissolveSingletonGroups(filters: Filter[]): Filter[] {
  const counts = new Map<number, number>()
  for (const f of filters) {
    if (f.groupId != null) counts.set(f.groupId, (counts.get(f.groupId) ?? 0) + 1)
  }
  return filters.map((f) =>
    f.groupId != null && counts.get(f.groupId) === 1 ? { ...f, groupId: undefined } : f,
  )
}

export function reseedFilters(
  stored: Filter[],
  seeds: Filter[],
  fields: readonly string[],
): Filter[] {
  const reseeded = new Set(fields)
  const pending = new Map(seeds.map((seed) => [seed.field, seed]))
  const kept = stored.flatMap((filter) => {
    if (!reseeded.has(filter.field)) return [filter]
    const seed = pending.get(filter.field)
    if (!seed) return []
    pending.delete(filter.field)
    return [{ ...seed, gate: filter.gate, groupId: filter.groupId }]
  })
  return normalizeGates(dissolveSingletonGroups([...pending.values(), ...kept]))
}

export function groupFiltersByDrag(
  filters: Filter[],
  draggedIndex: number,
  targetIndex: number,
): Filter[] {
  const dragged = filters[draggedIndex]
  const target = filters[targetIndex]
  if (!dragged || !target || draggedIndex === targetIndex) return filters
  if (dragged.groupId != null && dragged.groupId === target.groupId) return filters

  const groupId = target.groupId ?? nextGroupId(filters)
  const reassigned = filters.map((f, i) =>
    i === draggedIndex || i === targetIndex ? { ...f, groupId, gate: "or" as FilterGate } : f,
  )
  return normalizeGates(normalizeFilterGroups(dissolveSingletonGroups(reassigned)))
}

export function ungroupFilter(filters: Filter[], index: number): Filter[] {
  const filter = filters[index]
  if (!filter || filter.groupId == null) return filters
  const cleared = filters.map((f, i) => (i === index ? { ...f, groupId: undefined } : f))
  return normalizeGates(normalizeFilterGroups(dissolveSingletonGroups(cleared)))
}

export function reconcileGroupsByPosition(filters: Filter[]): Filter[] {
  const result = filters.map((f) => ({ ...f }))

  for (let i = 1; i < result.length - 1; i++) {
    const left = result[i - 1]!.groupId
    const right = result[i + 1]!.groupId
    if (left != null && left === right) result[i]!.groupId = left
  }

  let nextId = 1
  let i = 0
  while (i < result.length) {
    const gid = result[i]!.groupId
    if (gid == null) {
      i++
      continue
    }
    let j = i + 1
    while (j < result.length && result[j]!.groupId === gid) j++
    if (j - i >= 2) {
      const id = nextId++
      for (let k = i; k < j; k++) result[k]!.groupId = id
    } else {
      result[i]!.groupId = undefined
    }
    i = j
  }
  return result
}

export function moveFilter(filters: Filter[], from: number, insertIndex: number): Filter[] {
  if (from < 0 || from >= filters.length) return filters
  const next = filters.slice()
  const [moved] = next.splice(from, 1)
  if (!moved) return filters
  const clamped = Math.max(0, Math.min(insertIndex, next.length))
  next.splice(clamped, 0, { ...moved, groupId: undefined })
  return normalizeGates(reconcileGroupsByPosition(next))
}

export function normalizeFilterGroups(filters: Filter[]): Filter[] {
  const result: Filter[] = []
  const lastIndexByGroup = new Map<number, number>()
  for (const filter of filters) {
    const groupId = filter.groupId
    if (groupId != null && lastIndexByGroup.has(groupId)) {
      const insertAt = lastIndexByGroup.get(groupId)! + 1
      result.splice(insertAt, 0, filter)
      for (const [id, index] of lastIndexByGroup) {
        if (index >= insertAt) lastIndexByGroup.set(id, index + 1)
      }
      lastIndexByGroup.set(groupId, insertAt)
    } else {
      result.push(filter)
      if (groupId != null) lastIndexByGroup.set(groupId, result.length - 1)
    }
  }
  return result
}

export function applyGatedFilters<T>(
  rows: T[],
  filters: Filter[],
  applyConjunction: (rows: T[], conjunction: Filter[]) => T[],
): T[] {
  const units = splitFiltersIntoGroupUnits(filters)

  const unitRuns: FilterGroupUnit[][] = []
  for (const unit of units) {
    const lastRun = unitRuns[unitRuns.length - 1]
    if (!lastRun || unit.gate === "or") {
      unitRuns.push([unit])
    } else {
      lastRun.push(unit)
    }
  }

  const matched = new Set<T>()
  for (const run of unitRuns) {
    let result = rows
    for (const unit of run) {
      const innerGroups = splitFiltersIntoOrGroups(unit.filters)
      if (innerGroups.length === 1) {
        result = applyConjunction(result, unit.filters)
      } else {
        const unitMatched = new Set<T>()
        for (const inner of innerGroups) {
          for (const row of applyConjunction(result, inner)) unitMatched.add(row)
        }
        result = result.filter((row) => unitMatched.has(row))
      }
    }
    for (const row of result) matched.add(row)
  }
  return rows.filter((row) => matched.has(row))
}

// Shared comparison helpers used by applyFilter and applyNpiFilter
export function compareString(
  str: string,
  operator: string,
  value: string,
  values: string[],
): boolean {
  const strLower = str.toLowerCase()
  const singleValue = value.toLowerCase()
  const multipleValues = values.map((x) => x.toLowerCase())

  switch (operator) {
    case "include":
    case "=":
      return strLower === singleValue
    case "exclude":
    case "!=":
      return strLower !== singleValue
    case "startsWith":
      return strLower.startsWith(singleValue)
    case "endsWith":
      return strLower.endsWith(singleValue)
    case "contains":
      return strLower.includes(singleValue)
    case "doesNotContain":
      return !strLower.includes(singleValue)
    case "oneOf":
      return multipleValues.includes(strLower)
    case "notOneOf":
      return !multipleValues.includes(strLower)
    default:
      return false
  }
}

function compareNumber(num: number, operator: string, value: string): boolean {
  const valueAsNumber = Number(value)
  switch (operator) {
    case "=":
      return num === valueAsNumber
    case "!=":
      return num !== valueAsNumber
    case ">":
      return num > valueAsNumber
    case "<":
      return num < valueAsNumber
    case ">=":
      return num >= valueAsNumber
    case "<=":
      return num <= valueAsNumber
    default:
      return false
  }
}

function compareArray(arr: unknown[], operator: string, values: string[]): boolean {
  const lowerValues = values.map((v) => v.toLowerCase())
  switch (operator) {
    case "oneOf":
      return arr.some((item) => lowerValues.includes(String(item).toLowerCase()))
    case "notOneOf":
      return !arr.some((item) => lowerValues.includes(String(item).toLowerCase()))
    default:
      return false
  }
}

const NEGATION_OPERATORS = new Set(["!=", "notOneOf", "doesNotContain", "exclude"])

// Type-aware comparison of a single scalar value. `resolvedType` (when provided)
// wins over runtime `typeof` so number/date columns whose JSON values arrive as
// strings still dispatch to the right comparator.
export function compareScalar(
  fieldValue: unknown,
  operator: string,
  filter: { value: string; values: string[] },
  resolvedType?: string,
): boolean {
  if (operator === "isEmpty") return fieldValue == null || fieldValue === ""
  if (operator === "isNotEmpty") return fieldValue != null && fieldValue !== ""

  if (fieldValue == null) return NEGATION_OPERATORS.has(operator)

  // oneOf/notOneOf compare stringified values regardless of resolved type — the
  // filter UI sends `values: string[]`, so number columns get e.g. ["100","300"].
  if (operator === "oneOf" || operator === "notOneOf") {
    return compareString(String(fieldValue), operator, filter.value, filter.values)
  }

  if (resolvedType === "number") {
    const num = typeof fieldValue === "number" ? fieldValue : Number(fieldValue)
    if (Number.isNaN(num)) return false
    return compareNumber(num, operator, filter.value)
  }

  if (resolvedType === "date" || operator === "before" || operator === "after") {
    const fieldDate = fieldValue instanceof Date ? fieldValue : new Date(String(fieldValue))
    const valueDate = new Date(filter.value)
    if (!isValid(fieldDate) || !isValid(valueDate)) return false
    if (operator === "before") return isBefore(fieldDate, valueDate)
    if (operator === "after") return isAfter(fieldDate, valueDate)
    return false
  }

  if (typeof fieldValue === "boolean") {
    const filterBool = filter.value.toLowerCase() === "true"
    return operator === "=" ? fieldValue === filterBool : fieldValue !== filterBool
  }
  if (typeof fieldValue === "number") {
    return compareNumber(fieldValue, operator, filter.value)
  }
  return compareString(String(fieldValue), operator, filter.value, filter.values)
}

// Element-wise dispatch for array-shaped fieldValues (collection-extradata,
// incoming-order-extradata). Positive operators match if any element passes;
// negation operators only match when every element passes — i.e. having a
// single matching collection should disqualify a `!= x` filter.
export function compareScalarArray(
  arr: unknown[],
  operator: string,
  filter: { value: string; values: string[] },
  resolvedType?: string,
): boolean {
  if (operator === "isEmpty") return arr.length === 0 || arr.every((v) => v == null || v === "")
  if (operator === "isNotEmpty") return arr.some((v) => v != null && v !== "")
  if (arr.length === 0) return NEGATION_OPERATORS.has(operator)
  const compareFunction = (item: unknown) => compareScalar(item, operator, filter, resolvedType)
  return NEGATION_OPERATORS.has(operator) ? arr.every(compareFunction) : arr.some(compareFunction)
}

type PickedProductSummary = Pick<
  IVariantSummary,
  | "forecastQuantity"
  | "salesQuantity"
  | "preOrderQuantity"
  | "lostSalesWithinLeadTime"
  | "productId"
  | "productName"
  | "brand"
  | "category"
  | "color"
  | "gender"
  | "variantName"
  | "variantId"
  | "suggestedOrder"
  | "tags"
  | "vendors"
  | "preferredVendorId"
  | "abc"
  | "stockLevel"
  | "incomingStock"
  | "model"
  | "collections"
  | "soldSince"
  | "productType"
>

type OtherFilterKeyOptions =
  | "outOfStock"
  | "soonOutOfStock"
  | "withOrderSuggestion"
  | "hasTag"
  | "phasingOut"
  | "phasedOut"
  | "lifecycleStatus"
  | "isOverstocked"
  | "hasForecastAdjustment"
  | "isNpi"
  | "weekCover"
  | "inventoryTurn"
  | "gmroi"
  | "weeksOnHand"
  | "forecastQuantity"
  | "expectedGrowth"
  | "expectedGrowthUnits"
  | "demandVariantType"
  | "activatedFrom"
  | "activatedUntil"
  | ForecastChangeFieldKey

/**
 * A row carries `netForecastQuantity` only on pages that expose the gross/net demand toggle, and
 * only while it reads net. Filtering the demand a planner can actually see means preferring it.
 * `forecastChanges` is attached only where a comparison forecast exists (NOOS Forecast Updates),
 * so the month-change fields are exposed there via `extraFilterFields`, never globally.
 */
export type FilterableVariant = IVariantSummary & {
  netForecastQuantity?: number
  forecastChanges?: ForecastChanges
  demandVariantType?: DemandVariantType
}

export const NEW_VARIANT_LABEL = "New variant"
export const CARRY_OVER_LABEL = "Carry-over"
export type DemandVariantType = typeof NEW_VARIANT_LABEL | typeof CARRY_OVER_LABEL
export const variantTypeLabel = (isNew: boolean): DemandVariantType =>
  isNew ? NEW_VARIANT_LABEL : CARRY_OVER_LABEL

// Values the Type filter stored under earlier labels; saved views and localStorage still hold them.
const LEGACY_VARIANT_TYPE_VALUES: Record<string, DemandVariantType> = { NPI: NEW_VARIANT_LABEL }
const currentVariantTypeValue = (v: string) => LEGACY_VARIANT_TYPE_VALUES[v] ?? v
const hasLegacyVariantTypeValue = (f: Filter) =>
  f.field === "demandVariantType" &&
  [f.value, ...f.values].some((v) => v in LEGACY_VARIANT_TYPE_VALUES)

/** Rewrites stored Type filter values to the current labels. Same array back when none are stale. */
export const upgradeLegacyFilterValues = (filters: Filter[]): Filter[] =>
  filters.some(hasLegacyVariantTypeValue)
    ? filters.map((f) =>
        hasLegacyVariantTypeValue(f)
          ? {
              ...f,
              value: currentVariantTypeValue(f.value),
              values: f.values.map(currentVariantTypeValue),
            }
          : f,
      )
    : filters

// Filters compare the rounded value the cell displays, so e.g. "greater than 5" can never
// exclude a cell reading 5%. `undefined` exactly when the cell reads "-".
const changePctFilter = (label: ForecastRangeLabel) => (row: FilterableVariant) => {
  const pct = changePct(row.forecastChanges?.[label])
  return pct === null ? undefined : roundNumber(pct, 0)
}

// Unit cells render `formatForecast(value, 0)`; roundForecast-then-round reproduces that number.
const unitChangeFilter = (label: ForecastRangeLabel) => (row: FilterableVariant) => {
  const change = row.forecastChanges?.[label]?.change
  return change == null ? undefined : roundNumber(roundForecast(change), 0)
}

const forecastChangeFilters = Object.fromEntries(
  forecastChangeRanges.flatMap(({ label, pctKey, unitKey }) => [
    [pctKey, changePctFilter(label)],
    [unitKey, unitChangeFilter(label)],
  ]),
) as Record<ForecastChangeFieldKey, (row: FilterableVariant) => number | undefined>

const otherFilter: { [K in OtherFilterKeyOptions]: Function } = {
  outOfStock: (row: IVariantSummary) =>
    row.stockLevel === 0 || row.outOfStockInformation?.some((o) => o.status === "stockout"),
  // TODO: Should be renamed. OBS: This should also be done in the database for users that is already using this filter
  soonOutOfStock: (row: IVariantSummary) =>
    row.outOfStockInformation?.some((o) => o.status === "stockout" || o.status === "reorder"),
  withOrderSuggestion: (row: IVariantSummary) => row.suggestedOrder > 0,
  hasTag: (row: IVariantSummary) => row.tags,
  phasingOut: (row: IVariantSummary) => row.settings.lifecycleStatus === "phasingOut",
  phasedOut: (row: IVariantSummary) => row.settings.lifecycleStatus === "phasedOut",
  lifecycleStatus: (row: IVariantSummary) =>
    formatLifecycleStatus(deriveLifecycleStage(row.settings)),
  isOverstocked: (row: IVariantSummary) => {
    if (!row.outOfStockInformation.length) return row.stockLevel > 0
    return row.outOfStockInformation.some((o) => o.status === "overstock")
  },
  hasForecastAdjustment: (row: IVariantSummary) => row.adjustmentCount > 0,
  isNpi: (row: IVariantSummary) => row.isNpi,
  weekCover: (row: IVariantSummary) =>
    row.settings.bufferType === "weekCover" ? row.settings.bufferValue : undefined,
  inventoryTurn: (row: IVariantSummary) => {
    const cogs = row.inventoryTurnCogs ?? 0
    const avgInv = row.inventoryTurnAvgInvValue ?? 0
    return avgInv > 0 ? cogs / avgInv : undefined
  },
  gmroi: (row: IVariantSummary) => {
    const revenue = row.revenue ?? 0
    const cogs = row.inventoryTurnCogs ?? 0
    const avgInv = row.inventoryTurnAvgInvValue ?? 0
    return avgInv > 0 ? (revenue - cogs) / avgInv : undefined
  },
  // Filters compare the clamped, rounded value the cell displays. `undefined` exactly when
  // the cell reads "N/A" (no forecast data).
  weeksOnHand: (row: IVariantSummary) => {
    const raw = calculateWeeksOnHand(row.variantForecast, row.stockLevel, 1)
    return isFinite(raw) ? roundNumber(Math.min(52, Math.max(0, raw)), 1) : undefined
  },
  forecastQuantity: (row: FilterableVariant) => row.netForecastQuantity ?? row.forecastQuantity,
  // Whole percents, rounded as the cell renders it, so "less than -90" matches a cell reading -90%.
  expectedGrowth: (row: IVariantSummary) => {
    if (isLifecyclePhasedOut([row])) return undefined
    const growth = expectedGrowthFraction(
      row.salesQuantity ?? 0,
      row.forecastQuantity ?? 0,
      row.soldSince,
    )
    return growth === undefined ? undefined : roundNumber(growth * 100, 1)
  },
  expectedGrowthUnits: (row: IVariantSummary) =>
    expectedGrowthUnits(row.salesQuantity ?? 0, row.forecastQuantity ?? 0),
  // Attached by the Demand tab. Sibling tabs share the filter but not the value, so fall back to the SKU flag.
  demandVariantType: (row: FilterableVariant): DemandVariantType =>
    row.demandVariantType ??
    variantTypeLabel(!!row.isNpi && !isInactiveStatus(row.settings?.lifecycleStatus)),
  activatedFrom: (row: IVariantSummary) => row.settings.activatedFrom ?? undefined,
  activatedUntil: (row: IVariantSummary) => row.settings.activatedUntil ?? undefined,
  ...forecastChangeFilters,
}

export type FilterFieldType = {
  value: OtherFilterKeyOptions | keyof PickedProductSummary | string
  dynamicValue?: string
  name: string
  type: string
  groupBy?: string
  icon?: React.ReactNode
  /**
   * The values a "one of" pick offers, for a page-specific field whose values no variant read carries
   * (e.g. the Demand tab's own insight answers). Takes precedence over every derived option source.
   */
  options?: readonly string[]
  /** Shown inside a number field's value input, e.g. "%", so a threshold reads in its unit. */
  unit?: string
}

export const filterByLabels = [
  { value: "productId", label: "Product" },
  { value: "variantId", label: "Variant" },
]

export const filterFields: FilterFieldType[] = [
  { value: "brand", name: "Brand", type: "enum" },
  // eslint-disable-next-line @hakio/no-untranslated-terminology
  { value: "category", name: "Category", type: "enum", icon: <CoatHanger /> },
  { value: "gender", name: "Gender", type: "enum", icon: <Users /> },
  { value: "color", name: "Color", type: "enum", icon: <Palette /> },
  {
    value: "forecastQuantity",
    name: "Demand",
    type: "number",
    groupBy: "sum",
    icon: <ChartLine />,
  },
  // No groupBy: a ratio cannot be summed, and summing would also resurrect rows whose value is
  // undefined as 0 — see the `groupBy: "sum"` branch in applyFilter.
  { value: "expectedGrowth", name: "Expected growth (%)", type: "number", icon: <Percent /> },
  {
    value: "expectedGrowthUnits",
    name: "Expected growth (units)",
    type: "number",
    icon: <TrendUp />,
  },
  { value: "outOfStock", name: "Out of stock", type: "boolean", icon: <Warning /> },
  { value: "productId", name: "Product ID", type: "string", icon: <Hash /> },
  { value: "productName", name: "Product name", type: "string", icon: <TextT /> },
  {
    value: "salesQuantity",
    // eslint-disable-next-line @hakio/no-untranslated-terminology
    name: "Sales",
    type: "number",
    groupBy: "sum",
    icon: <TrendUp />,
  },
  {
    value: "preOrderQuantity",
    // eslint-disable-next-line @hakio/no-untranslated-terminology
    name: "Preorder",
    type: "number",
    groupBy: "sum",
    icon: <ShoppingCartSimple />,
  },
  {
    value: "lostSalesWithinLeadTime",
    // eslint-disable-next-line @hakio/no-untranslated-terminology
    name: "Lost Sales",
    type: "number",
    groupBy: "sum",
    icon: <TrendDown />,
  },
  {
    value: "incomingStock",
    name: "Incoming stock",
    type: "number",
    groupBy: "sum",
    icon: <Package />,
  },
  {
    value: "suggestedOrder",
    name: "Suggested order",
    type: "number",
    groupBy: "sum",
    icon: <ShoppingCartSimple />,
  },
  { value: "soonOutOfStock", name: "Reorder now", type: "boolean", icon: <Warning /> },
  // TODO(BUD-370): rename to "Current lifecycle status".
  { value: "lifecycleStatus", name: "Lifecycle status", type: "enum", icon: <Flag /> },
  { value: "tags", name: "Tags", type: "tag", icon: <Tag /> },
  // TODO: Consider if we should add string[] as an explicit type
  { value: "variantName", name: "Variant names", type: "string", icon: <TextT /> },
  { value: "variantId", name: "Variant ID", type: "string", icon: <Hash /> },
  { value: "vendors", name: "Vendor", type: "enum", icon: <Factory /> },
  { value: "preferredVendorId", name: "Preferred Vendor", type: "enum", icon: <Factory /> },
  {
    value: "withOrderSuggestion",
    name: "Has order suggestion",
    type: "boolean",
    icon: <ShoppingCartSimple />,
  }, // This is referred to elsewhere so don't change the value.
  { value: "abc", name: "ABC", type: "enum", icon: <TreeStructure /> },
  { value: "stockLevel", name: "Stock level", type: "number", groupBy: "sum", icon: <Package /> },
  { value: "isOverstocked", name: "Overstocked", type: "boolean", icon: <Warning /> },
  {
    value: "hasForecastAdjustment",
    name: "Has demand adjustment",
    type: "boolean",
    icon: <Sliders />,
  },
  { value: "isNpi", name: "NPI", type: "boolean", icon: <Sparkle /> },
  { value: "model", name: "Forecast model", type: "string", icon: <ChartBar /> },
  { value: "collections", name: "Collection", type: "collection", icon: <TShirt /> },
  { value: "soldSince", name: "Sold since", type: "date", icon: <CalendarBlank /> },
  { value: "etd", name: "ETD", type: "date", icon: <CalendarBlank /> },
  { value: "activatedFrom", name: "Activated from", type: "date", icon: <CalendarBlank /> },
  { value: "activatedUntil", name: "Activated until", type: "date", icon: <CalendarBlank /> },
  { value: "productType", name: "Product type", type: "enum" },
  // eslint-disable-next-line @hakio/no-untranslated-terminology
  { value: "userId", name: "Planner", type: "user", icon: <User /> },
  { value: "weekCover", name: "Week cover", type: "number", icon: <Timer /> },
  { value: "weeksOnHand", name: "Weeks on hand", type: "number", icon: <Hourglass /> },
  { value: "inventoryTurn", name: "Inventory turn", type: "number", icon: <ArrowsClockwise /> },
  { value: "gmroi", name: "GMROI", type: "number", icon: <ArrowsClockwise /> },
]

/**
 * SKU-level "Size" filter. Unlike every entry in {@link filterFields} (variant-level), this
 * matches on a SKU's size and is applied at the SKU-row level by the Fashion Supply Plan. It is
 * NOT part of the global `filterFields` — callers opt in via `customFilterFields` so it only
 * appears where SKU rows exist.
 */
export const SKU_SIZE_FILTER_FIELD = "skuSize"

export const skuSizeFilterField: FilterFieldType = {
  value: SKU_SIZE_FILTER_FIELD,
  name: "SKU Size",
  type: "string",
  icon: <Ruler />,
}

// Named with the column labels directly: importing the column naming here is an import cycle.
export const SKU_SIZE_DIMENSION_1_FILTER_FIELD = "skuSizeDimension1"
export const SKU_SIZE_DIMENSION_2_FILTER_FIELD = "skuSizeDimension2"

export const skuSizeDimensionFilterFields: FilterFieldType[] = [
  {
    value: SKU_SIZE_DIMENSION_1_FILTER_FIELD,
    name: SIZE_DIMENSION_1_LABEL,
    type: "string",
    icon: <Ruler />,
  },
  {
    value: SKU_SIZE_DIMENSION_2_FILTER_FIELD,
    name: SIZE_DIMENSION_2_LABEL,
    type: "string",
    icon: <Ruler />,
  },
]

const SKU_FILTER_ROW_KEYS = {
  [SKU_SIZE_FILTER_FIELD]: "size",
  [SKU_SIZE_DIMENSION_1_FILTER_FIELD]: "sizeDimension1",
  [SKU_SIZE_DIMENSION_2_FILTER_FIELD]: "sizeDimension2",
} as const

export const SKU_LEVEL_FILTER_FIELDS: ReadonlySet<string> = new Set(
  Object.keys(SKU_FILTER_ROW_KEYS),
)

type SkuFilterRow = {
  size?: string | null
  sizeDimension1?: string | null
  sizeDimension2?: string | null
}

/** The SKU row value a SKU-level filter matches on. */
export function getSkuFilterValue(row: SkuFilterRow, field: string): string | null {
  const key = SKU_FILTER_ROW_KEYS[field as keyof typeof SKU_FILTER_ROW_KEYS]
  return key ? (row[key] ?? null) : null
}

const SKU_FILTER_FIELDS = [skuSizeFilterField]
const SKU_FILTER_FIELDS_WITH_SIZE_DIMENSIONS = [skuSizeFilterField, ...skuSizeDimensionFilterFields]

const SIZE_DIMENSION_FILTER_FIELDS: ReadonlySet<string> = new Set([
  SKU_SIZE_DIMENSION_1_FILTER_FIELD,
  SKU_SIZE_DIMENSION_2_FILTER_FIELD,
])

/** Drops size dimension filters while the flag is off, so a saved one can't scope rows unseen. */
export function withEnabledSkuFilters<T extends { field: string }>(
  filters: T[],
  sizeDimensionsEnabled: boolean,
): T[] {
  if (sizeDimensionsEnabled) return filters
  return filters.filter((f) => !SIZE_DIMENSION_FILTER_FIELDS.has(f.field))
}

/** Stable refs, so the filter popover's field memo doesn't churn each render. */
export function getSkuFilterFields(sizeDimensionsEnabled: boolean): FilterFieldType[] {
  return sizeDimensionsEnabled ? SKU_FILTER_FIELDS_WITH_SIZE_DIMENSIONS : SKU_FILTER_FIELDS
}

/**
 * Month-change fields for the Forecast Updates page. NOT part of the global `filterFields` —
 * their values compare against a historic forecast snapshot that only that page loads, so it
 * opts in via `extraFilterFields`. Field keys match the table's colIds and persist in saved
 * views — keep them stable. No `groupBy`: a ratio cannot be summed, and the `groupBy: "sum"`
 * branch in applyFilter would resurrect rows whose value is undefined as 0.
 *
 * Names carry the `(%)` / `(units)` suffix instead of the columns' "% change" phrasing: the
 * popover renders names through `startCase`, which strips `%` and would collide the pair.
 */
export const forecastChangeFilterFields: FilterFieldType[] = forecastChangeRanges.flatMap(
  ({ name, pctKey, unitKey }) => [
    { value: pctKey, name: `${name} change (%)`, type: "number", icon: <Percent /> },
    { value: unitKey, name: `${name} change (units)`, type: "number", icon: <TrendUp /> },
  ],
)

export type FilterOperatorType = {
  value: string
  name: string
  type: string[]
  arrayValue?: boolean
  valueless?: boolean
}

const filterOperators: FilterOperatorType[] = [
  {
    name: "Is one of",
    value: "oneOf",
    type: ["string", "tag", "enum", "collection"],
    arrayValue: true,
  },
  {
    name: "Is not one of",
    value: "notOneOf",
    type: ["string", "tag", "enum", "collection"],
    arrayValue: true,
  },
  { name: "Is equal to", value: "=", type: ["number", "string", "boolean"] },
  { name: "Is not equal to", value: "!=", type: ["number", "string", "boolean"] },
  { name: "Greater than", value: ">", type: ["number"] },
  { name: "Lesser than", value: "<", type: ["number"] },
  { name: "Greater than or equal to", value: ">=", type: ["number"] },
  { name: "Lesser than or equal to", value: "<=", type: ["number"] },
  { name: "Ends with", value: "endsWith", type: ["string"] },
  { name: "Starts with", value: "startsWith", type: ["string"] },
  { name: "Contains", value: "contains", type: ["string"] },
  { name: "Does not contain", value: "doesNotContain", type: ["string"] },
  { name: "Include", value: "include", type: ["user"] },
  { name: "Exclude", value: "exclude", type: ["user"] },
  { name: "Before", value: "before", type: ["date"] },
  { name: "After", value: "after", type: ["date"] },
  { name: "Is empty", value: "isEmpty", type: ["date", "string"], valueless: true },
  {
    name: "Is not empty",
    value: "isNotEmpty",
    type: ["date", "string"],
    valueless: true,
  },
]

// Operators that match on the *presence* of a value rather than its content.
// They must be evaluated before any null-short-circuit and bypass the
// "drop undefined fieldValues" guard in applyFilter. Excluded from number
// fields: all numeric fields are summed per variant, and a sum is never null.
const VALUELESS_OPERATORS = new Set(filterOperators.filter((o) => o.valueless).map((o) => o.value))

export function applyFilter(
  objects: IVariantSummary[],
  filter: Filter,
  allFilters?: Filter[],
  getFieldType?: (field: string) => string | undefined,
  scopedCollectionIds?: number[],
) {
  const collectionExtraDataKey = filter.field.replace("(collection-extradata)", "")
  const activeCollectionNames = allFilters
    ?.filter((f) => f.field === "collections" && f.operator === "oneOf")
    .flatMap((f) => f.values.map((v) => v.toLowerCase()))
  const scopedCollectionIdSet =
    scopedCollectionIds && scopedCollectionIds.length > 0 ? new Set(scopedCollectionIds) : undefined

  // filter.by is a runtime-selected field name; lodash gave no compile-time verification here either.
  const objectsBy = groupBy(objects, filter.by as any)

  return flatten(
    Object.values(objectsBy).filter((objects) => {
      const field = filter.field as keyof PickedProductSummary
      const staticFilterType = filterFields.find((filterField) => filterField.value === field)?.type
      const resolvedType = getFieldType?.(filter.field) ?? staticFilterType
      const operator = filter.operator

      let fieldValues = objects
        .map((object) => {
          let fieldValue = object[field]
          if (field === "preferredVendorId") {
            const id = object.settings?.preferredVendorId
            fieldValue = id ? (object.vendors.find((v) => v.id === id)?.name ?? id) : undefined
          } else if (field in otherFilter) {
            // Ahead of the plain row lookup so a field can be derived even when a row property of
            // the same name exists — that is how demand resolves to its net value.
            fieldValue = otherFilter[field as OtherFilterKeyOptions](object)
          } else if (field in object) {
            fieldValue = object[field]
          } else if (
            object.extraData &&
            field.replace("(extradata)", "") in (object.extraData as any)
          ) {
            const key = field.replace("(extradata)", "")
            // Match against every underlying SKU's value when available, not just
            // object.extraData (which collapses to one arbitrary SKU via FIRST() in
            // variantTableData.ts) — a variant sourced from multiple vendors/manufacturers
            // must match if ANY SKU matches (SLIM-657). Falls back to the single collapsed
            // value when extraDataValues wasn't fetched (e.g. tests, older callers).
            fieldValue = object.extraDataValues
              ? object.extraDataValues.map((ed) => (ed as any)?.[key]).filter((v) => v != null)
              : (object.extraData as any)[key]
          } else if (
            object.collections?.some((x) => x.extraData && collectionExtraDataKey in x.extraData)
          ) {
            let scopedCollections = object.collections
            if (scopedCollectionIdSet) {
              scopedCollections = scopedCollections.filter((c) =>
                scopedCollectionIdSet.has(Number(c.id)),
              )
            }
            if (activeCollectionNames?.length) {
              scopedCollections = scopedCollections.filter((c) =>
                activeCollectionNames.includes(c.name.toLowerCase()),
              )
            }
            const exists = scopedCollections.filter(
              (x) => x.extraData && collectionExtraDataKey in x.extraData,
            )
            // Same reasoning as the extraData branch above, applied per collection.
            fieldValue = exists
              .flatMap((x) =>
                x.extraDataValues
                  ? x.extraDataValues.map((ed) => (ed as any)?.[collectionExtraDataKey])
                  : [x.extraData[collectionExtraDataKey]],
              )
              .filter((v) => v != null)
          } else if (
            field.includes("(incoming-order-extradata)") &&
            object.incomingOrders?.length
          ) {
            const key = field.replace("(incoming-order-extradata)", "")
            fieldValue = object.incomingOrders
              .map((order) => (order.extraData as any)?.[key])
              .filter((v) => v != null)
          }
          return fieldValue
        })
        .filter((fieldValue) =>
          operator !== "notOneOf" && operator !== "!=" && !VALUELESS_OPERATORS.has(operator)
            ? fieldValue !== undefined
            : true,
        )

      if (filterFields.find((f) => f.value === field)?.groupBy === "sum") {
        fieldValues = [sum(fieldValues.filter(Boolean))]
      }

      const res = fieldValues.some((fieldValue) => {
        const tagComparison = (tags: any) => {
          switch (operator) {
            case "include":
              return tags.some((tag: any) => tag?.name === filter.value)
            case "exclude":
              return !tags.some((tag: any) => tag?.name == filter.value)
            case "oneOf":
              return tags.some((tag: any) => filter.values.includes(tag?.name))
            case "notOneOf":
              return !tags.some((tag: any) => filter.values.includes(tag?.name))
            default:
          }
        }

        const enumLookup = ["brand", "category", "productType"]

        if (field === "tags" && Array.isArray(fieldValue)) {
          return tagComparison(fieldValue)
        }
        // Dropdown enums logic
        else if (enumLookup.includes(field) && typeof fieldValue === "string") {
          return compareString(fieldValue, operator, filter.value, filter.values)
        } else if (field === "collections" && Array.isArray(fieldValue) && fieldValue.length > 0) {
          const names = (fieldValue as { id: number; name: string }[]).map((v) => v.name)
          if (operator === "notOneOf") {
            return names.every((str) => compareString(str, operator, filter.value, filter.values))
          }
          return names.some((str) => compareString(str, operator, filter.value, filter.values))
        } else if (field === "vendors") {
          const names = (fieldValue as { id: string; name: string }[]).map((v) => v.name)
          if (operator === "notOneOf") {
            return names.every((str) => compareString(str, operator, filter.value, filter.values))
          }
          return names.some((str) => compareString(str, operator, filter.value, filter.values))
        } else if (Array.isArray(fieldValue)) {
          // collection-extradata / incoming-order-extradata: dispatch per element
          // using the resolved field type. Fixes zero-results when comparing array
          // values with operators outside oneOf/notOneOf.
          return compareScalarArray(fieldValue, operator, filter, resolvedType)
        }
        return compareScalar(fieldValue, operator, filter, resolvedType)
      })

      return res
    }),
  )
}

const getFilterType = (fieldValue: string, manualFields?: FilterFieldType[]): string => {
  if (manualFields != null) {
    return (
      manualFields.find((f) => {
        let value = f.value
        if (f.value === "DynamicFilterField" && f.dynamicValue) {
          value = f.dynamicValue
        }
        return fieldValue === value
      })?.type ?? "string"
    )
  }
  return filterFields.find((f) => f.value === fieldValue)?.type ?? "string"
}

export const getFilterIcon = (fieldValue: string, fields: FilterFieldType[] = filterFields) => {
  return fields.find((f) => f.value === fieldValue)?.icon ?? <Tag />
}

export const isTagFiltered = (filters: Filter[], tagName: string): boolean =>
  filters.some((f) => f.field === "tags" && f.values.includes(tagName))

export const toggleTagInFilters = (filters: Filter[], tagName: string): Filter[] => {
  if (isTagFiltered(filters, tagName)) {
    return filters
      .map((f) =>
        f.field === "tags" ? { ...f, values: f.values.filter((v) => v !== tagName) } : f,
      )
      .filter((f) => f.field !== "tags" || f.values.length > 0)
  }
  const tagFilter = filters.find((f) => f.field === "tags" && f.operator === "oneOf")
  if (tagFilter) {
    return filters.map((f) => (f === tagFilter ? { ...f, values: [...f.values, tagName] } : f))
  }
  return [
    ...filters,
    { field: "tags", operator: "oneOf", value: "", values: [tagName], by: "variantId" },
  ]
}

export const filterVariants = (
  filters: Filter[],
  variants: IVariantSummary[],
  searchText?: string | undefined,
  getFieldType?: (field: string) => string | undefined,
  scopedCollectionIds?: number[],
) => {
  if (variants) {
    let filteredVariants = variants

    if (searchText !== undefined && searchText !== "") {
      filteredVariants = filterOnSearchString(filteredVariants, searchText)
    }

    if (filters && filters.length > 0 && hasAdvancedFilterExpression(filters)) {
      return applyGatedFilters(filteredVariants, filters, (rows, conjunction) => {
        const incomingFilters = getIncomingOrderExtraDataFilters(conjunction)
        const regularConjunctionFilters = conjunction.filter(
          (f) => !f.field.includes("(incoming-order-extradata)"),
        )
        let result = rows
        for (const filter of regularConjunctionFilters) {
          result = applyFilter(
            result,
            filter,
            regularConjunctionFilters,
            getFieldType,
            scopedCollectionIds,
          )
        }
        if (incomingFilters.length > 0) {
          result = result.filter(
            (variant) =>
              filterIncomingOrders(variant.incomingOrders ?? [], {
                extraDataFilters: incomingFilters,
              }).length > 0,
          )
        }
        return result
      })
    }

    if (filters && filters.length > 0) {
      const incomingExtraDataFilters = getIncomingOrderExtraDataFilters(filters)
      const regularFilters = filters.filter((f) => !f.field.includes("(incoming-order-extradata)"))

      for (const filter of regularFilters) {
        filteredVariants = applyFilter(
          filteredVariants,
          filter,
          regularFilters,
          getFieldType,
          scopedCollectionIds,
        )
      }

      if (incomingExtraDataFilters.length > 0) {
        // Delegate to filterIncomingOrders which handles all operators correctly.
        // Callers must merge arrived orders into variant.incomingOrders before calling
        // filterVariants if they want arrived orders to be matched.
        filteredVariants = filteredVariants.flatMap((variant) => {
          const filteredOrders = filterIncomingOrders(variant.incomingOrders ?? [], {
            extraDataFilters: incomingExtraDataFilters,
          })
          if (filteredOrders.length === 0) return []
          return [{ ...variant, incomingOrders: filteredOrders }]
        })
      }

      // Restrict vendors to matching ones so ProductTableV2's per-vendor row expansion only
      // creates rows for vendors that satisfy the filter. Also sync groupVendor with vendors[0].
      const vendorFilters = regularFilters.filter((f) => f.field === "vendors")
      if (vendorFilters.length > 0) {
        filteredVariants = filteredVariants.map((v) => {
          const matchingVendors = v.vendors.filter((vendor) =>
            vendorFilters.every((f) => compareString(vendor.name, f.operator, f.value, f.values)),
          )
          return {
            ...v,
            vendors: matchingVendors,
            groupVendor: matchingVendors[0]?.name,
          }
        })
      }
    }

    return filteredVariants
  }

  return []
}

export const disabledFilterFields = [
  "outOfStock",
  "soonOutOfStock",
  "isOverstocked",
  "withOrderSuggestion",
  "hasForecastAdjustment",
  "isNpi",
]

// Per-field status for filters that are backed by tenant-specific lookup data
// (brands, categories, vendors, collections, tags, forecast models, …).
// Used to hide filters whose value list has loaded and is empty for the current
// customer — but never while data is still pending.
export type FilterFieldDataStatus = {
  hasValues: boolean
  isLoading: boolean
}

export function computeHiddenFilterFields(
  sources: Record<string, FilterFieldDataStatus | undefined>,
): Set<string> {
  const hidden = new Set<string>()
  for (const [field, source] of Object.entries(sources)) {
    if (!source) continue
    if (!source.isLoading && !source.hasValues) {
      hidden.add(field)
    }
  }
  return hidden
}

export const sortFilterFields = (a: FilterFieldType, b: FilterFieldType) => {
  if (disabledFilterFields.includes(a.value)) return 2
  if (disabledFilterFields.includes(b.value)) return -2
  return a.name.localeCompare(b.name)
}

export const getFilterOperators = (field: string, manualFields?: FilterFieldType[]) =>
  filterOperators.filter((o) => o.type.includes(getFilterType(field, manualFields)))

// NPI-specific filter fields for INpiVariantSummary type
export const npiFilterFields: FilterFieldType[] = [
  { value: "brand", name: "Brand", type: "enum" },
  // eslint-disable-next-line @hakio/no-untranslated-terminology
  { value: "category", name: "Category", type: "enum", icon: <CoatHanger /> },
  { value: "gender", name: "Gender", type: "enum", icon: <Users /> },
  { value: "color", name: "Color", type: "enum", icon: <Palette /> },
  { value: "productId", name: "Product ID", type: "string", icon: <Hash /> },
  { value: "productName", name: "Product name", type: "string", icon: <TextT /> },
  { value: "variantName", name: "Variant name", type: "string", icon: <TextT /> },
  { value: "variantId", name: "Variant ID", type: "string", icon: <Hash /> },
  {
    value: "forecastQuantity",
    name: "NPI forecast",
    type: "number",
    groupBy: "sum",
    icon: <ChartLine />,
  },
  {
    value: "incomingStock",
    name: "Incoming stock",
    type: "number",
    groupBy: "sum",
    icon: <Package />,
  },
  { value: "stockLevel", name: "Stock level", type: "number", groupBy: "sum", icon: <Package /> },
  { value: "productType", name: "Product type", type: "enum" },
]

function applyNpiFilter(objects: INpiVariantSummary[], filter: Filter) {
  // filter.by is a runtime-selected field name; lodash gave no compile-time verification here either.
  const objectsBy = groupBy(objects, filter.by as any)

  return flatten(
    Object.values(objectsBy).filter((objects) => {
      const field = filter.field
      const operator = filter.operator
      const value = filter.value.toLowerCase()

      let fieldValues = objects
        .map((object) => {
          // Check direct fields
          if (field in object) {
            return object[field as keyof INpiVariantSummary]
          }
          // Check nested productType in settings
          if (field === "productType" && object.settings?.productType) {
            return object.settings.productType
          }
          // Check extraData fields
          if (
            object.extraData &&
            field.replace("(extradata)", "") in (object.extraData as Record<string, unknown>)
          ) {
            return (object.extraData as Record<string, unknown>)[field.replace("(extradata)", "")]
          }
          return undefined
        })
        .filter((fieldValue) =>
          operator !== "notOneOf" && operator !== "!=" && !VALUELESS_OPERATORS.has(operator)
            ? fieldValue !== undefined
            : true,
        )

      // Apply sum aggregation for numeric fields
      if (npiFilterFields.find((f) => f.value === field)?.groupBy === "sum") {
        fieldValues = [sum(fieldValues.filter(Boolean) as number[])]
      }

      return fieldValues.some((fieldValue) => {
        if (operator === "isEmpty") {
          return fieldValue == null || fieldValue === ""
        }
        if (operator === "isNotEmpty") {
          return fieldValue != null && fieldValue !== ""
        }
        if (typeof fieldValue === "string") {
          return compareString(fieldValue, operator, filter.value, filter.values)
        } else if (typeof fieldValue === "boolean") {
          const filterBool = value === "true"
          return operator === "=" ? fieldValue === filterBool : fieldValue !== filterBool
        } else if (typeof fieldValue === "number") {
          return compareNumber(fieldValue, operator, value)
        } else if (Array.isArray(fieldValue)) {
          return compareArray(fieldValue, operator, filter.values)
        } else if (
          (typeof fieldValue === "undefined" || fieldValue === null) &&
          (operator === "!=" || operator === "notOneOf")
        ) {
          return true
        }
        return false
      })
    }),
  )
}

export const filterNpiVariants = (
  filters: Filter[],
  variants: INpiVariantSummary[],
  searchText?: string,
) => {
  if (!variants) return []

  let filteredVariants = variants

  if (searchText !== undefined && searchText !== "") {
    filteredVariants = filterOnSearchString(filteredVariants, searchText)
  }

  if (filters && filters.length > 0) {
    if (hasAdvancedFilterExpression(filters)) {
      return applyGatedFilters(filteredVariants, filters, (rows, conjunction) => {
        let result = rows
        for (const filter of conjunction) {
          result = applyNpiFilter(result, filter)
        }
        return result
      })
    }

    for (const filter of filters) {
      filteredVariants = applyNpiFilter(filteredVariants, filter)
    }
  }

  return filteredVariants
}
